import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  buildCustomMintArgs,
  confirmCustomTokenAction,
  getCustomActionCapabilities,
  isCustomTokenInfo,
  prepareCustomTokenAction,
} from "../lib/customTokenActions";
import { useAuth } from "../auth/useAuth";
import {
  switchActiveWalletChain,
  sendActiveWalletTransaction,
} from "../wallet/walletClient";
import {
  hasPaidFeatureAccess,
  normalizePlanCode,
} from "../components/billing/plans";

/* ---------------------------------------------------------------------
 * Small Utilities
 * ------------------------------------------------------------------- */
function isNonEmptyString(v) {
  return typeof v === "string" && v.trim().length > 0;
}

function isEvmAddress(v) {
  return typeof v === "string" && /^0x[a-fA-F0-9]{40}$/.test(v.trim());
}

function normalizeTokenType(v) {
  const t = String(v || "").trim().toUpperCase();
  if (t === "ERC20" || t === "ERC721" || t === "ERC1155") return t;
  return null;
}

function shorten(value) {
  if (!value || typeof value !== "string") return "-";
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function safeApiError(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function humanizeWalletActionError(err, fallback = "Transaction failed.") {
  const code = err?.code;
  const nestedCode = err?.info?.error?.code;
  const message = String(
    err?.shortMessage ||
    err?.reason ||
    err?.message ||
    err?.info?.error?.message ||
    "",
  ).toLowerCase();

  const isRejected =
    code === "ACTION_REJECTED" ||
    code === 4001 ||
    nestedCode === 4001 ||
    nestedCode === 5000 ||
    message.includes("user denied") ||
    message.includes("user rejected") ||
    message.includes("rejected") ||
    message.includes("denied transaction signature");

  if (isRejected) {
    return "Mint was cancelled in your wallet.";
  }

  return safeApiError(err, fallback);
}

function humanizeBalanceError(err, fallback = "Unable to fetch balance.") {
  const message = String(
    err?.shortMessage ||
    err?.reason ||
    err?.message ||
    err?.info?.error?.message ||
    "",
  ).toLowerCase();

  const isBadData =
    message.includes("could not decode result data") ||
    message.includes("bad_data") ||
    message.includes("balanceof");

  if (isBadData) {
    return "Balance could not be read for this contract on the selected network. Check the token type and network, or load a compatible token.";
  }

  return safeApiError(err, fallback);
}

function isIntegerString(v) {
  if (!isNonEmptyString(v)) return false;
  return /^[0-9]+$/.test(v.trim());
}

function isHexBytes(v) {
  if (!isNonEmptyString(v)) return false;
  const s = v.trim();
  return /^0x([a-fA-F0-9]{2})*$/.test(s);
}

function eqAddr(a, b) {
  if (!a || !b) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

function readQueryParams() {
  try {
    const url = new URL(window.location.href);
    return {
      network: url.searchParams.get("network") || "",
      address: url.searchParams.get("address") || "",
    };
  } catch {
    return { network: "", address: "" };
  }
}

function safeJsonParse(s, fallback) {
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
}

const NETWORK_META = {
  ethereum: { logo: "/logos/ethereum.png", tone: "from-slate-100 to-slate-50" },
  sepolia: { logo: "/logos/ethereum.png", tone: "from-indigo-50 to-sky-50" },
  polygon: { logo: "/logos/polygon.png", tone: "from-purple-50 to-indigo-50" },
  arbitrum: { logo: "/logos/arbitrum.png", tone: "from-sky-50 to-blue-50" },
  avalanche: { logo: "/logos/avalanche.png", tone: "from-rose-50 to-red-50" },
  bnb: { logo: "/logos/bnb.png", tone: "from-amber-50 to-yellow-50" },
  base: { logo: "/logos/base.png", tone: "from-blue-50 to-cyan-50" },
  optimism: { logo: "/logos/optimism.png", tone: "from-rose-50 to-orange-50" },
};

function getNetworkMeta(network) {
  const key = String(network?.key || network?.label || "").trim().toLowerCase();

  if (key.includes("arbitrum")) return NETWORK_META.arbitrum;
  if (key.includes("avalanche")) return NETWORK_META.avalanche;
  if (key.includes("polygon") || key.includes("amoy")) return NETWORK_META.polygon;
  if (key.includes("bnb") || key.includes("bsc")) return NETWORK_META.bnb;
  if (key.includes("base")) return NETWORK_META.base;
  if (key.includes("optimism") || key.includes("op ")) return NETWORK_META.optimism;
  if (key.includes("ethereum")) return NETWORK_META.ethereum;
  if (key.includes("sepolia")) return NETWORK_META.sepolia;

  return { logo: "", tone: "from-slate-50 to-white" };
}

function isTestnetNetwork(network) {
  const value = String(network?.label || network?.key || "").trim().toLowerCase();
  return value.includes("testnet") || value.includes("sepolia") || value.includes("fuji");
}

function getNetworkShortName(network) {
  const label = String(network?.label || network?.key || "").trim().toUpperCase();
  if (label.includes("ETHEREUM")) return "ETH";
  if (label.includes("ARBITRUM")) return "ARB";
  if (label.includes("AVALANCHE")) return "AVAX";
  if (label.includes("POLYGON")) return "POL";
  if (label.includes("BNB") || label.includes("BSC")) return "BNB";
  if (label.includes("BASE")) return "BASE";
  if (label.includes("OP")) return "OP";
  return label.slice(0, 3) || "NET";
}

/* ---------------------------------------------------------------------
 * UI Primitives
 * ------------------------------------------------------------------- */
function cn(...values) {
  return values.filter(Boolean).join(" ");
}

function Surface({ className = "", children }) {
  return (
    <div
      className={cn(
        "rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.07)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function SectionCard({ title, subtitle, right, children, className = "" }) {
  return (
    <Surface
      className={cn(
        "overflow-hidden bg-white p-6",
        className,
      )}
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex rounded-full border border-[#E6D3A3] bg-[#FFF8E7] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9A7226]">
            Operations
          </div>

          <h2 className="mt-3 text-xl font-semibold tracking-[-0.035em] text-[#050713]">
            {title}
          </h2>

          {subtitle ? (
            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
              {subtitle}
            </p>
          ) : null}
        </div>

        {right ? <div className="shrink-0">{right}</div> : null}
      </div>

      {children}
    </Surface>
  );
}

function StatusPill({ label, value, tone = "neutral" }) {
  const toneClass =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "warning"
        ? "border-[#E6D3A3] bg-[#FFF8E7] text-[#8A641E]"
        : tone === "brand"
          ? "border-[#E6D3A3] bg-[#FFF8E7] text-[#8A641E]"
          : "border-white/10 bg-white/[0.07] text-slate-200";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur",
        toneClass,
      )}
    >
      <span className={tone === "neutral" ? "text-slate-400" : "opacity-70"}>
        {label}
      </span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function InlineNotice({ title, subtitle, tone = "neutral", actions = null }) {
  const toneClass =
    tone === "warning"
      ? "border-[#E6D3A3] bg-[#FFF8E7] text-[#4B3410]"
      : tone === "error"
        ? "border-red-200 bg-red-50 text-red-900"
        : tone === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
          : "border-slate-200 bg-slate-50 text-slate-900";

  return (
    <div className={cn("rounded-2xl border px-4 py-3 shadow-sm", toneClass)}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-sm font-semibold tracking-[-0.01em]">{title}</div>
          {subtitle ? (
            <div className="mt-1 text-xs leading-5 opacity-80">{subtitle}</div>
          ) : null}
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
      {children}
    </label>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#050713] outline-none transition",
        "placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20",
        className,
      )}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={cn(
        "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#050713] outline-none transition",
        "focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20",
        className,
      )}
    >
      {children}
    </select>
  );
}

function PrimaryButton({ className = "", children, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center rounded-2xl bg-[#050713] px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(5,7,19,0.22)] transition",
        "hover:-translate-y-0.5 hover:bg-[#111827] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ className = "", children, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm transition",
        "hover:-translate-y-0.5 hover:border-[#E6D3A3] hover:bg-[#FFF8E7] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  );
}



/* ---------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------- */
export default function Operations() {
  const { isAuthed, authLoading, restoreLoading, session } = useAuth();

  const walletAddress = useMemo(() => {
    if (!isAuthed) return null;
    const a = String(session?.address || "").trim();
    return isEvmAddress(a) ? a : null;
  }, [isAuthed, session]);


  const [accountUsage, setAccountUsage] = useState(null);
  const navigate = useNavigate();

  /* ---------------- Networks ---------------- */
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [networksLoading, setNetworksLoading] = useState(false);
  const [networksError, setNetworksError] = useState("");
  const [networkPickerOpen, setNetworkPickerOpen] = useState(false);
  const [networkPickerTab, setNetworkPickerTab] = useState("mainnets");

  /* ---------------- Contract ---------------- */
  const [contractAddress, setContractAddress] = useState("");

  /* ---------------- Token Detection + Info ---------------- */
  const [autoDetectEnabled, setAutoDetectEnabled] = useState(true);
  const [mintTokenType, setMintTokenType] = useState("ERC721");

  const [detectedTokenType, setDetectedTokenType] = useState(null);
  const [detectStatus, setDetectStatus] = useState("idle");
  const [detectMessage, setDetectMessage] = useState("");
  const detectReqIdRef = useRef(0);

  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenInfoLoading, setTokenInfoLoading] = useState(false);
  const [tokenInfoError, setTokenInfoError] = useState("");

  /* ---------------- Artifact Key ---------------- */
  const [artifactKeyLoading, setArtifactKeyLoading] = useState(false);
  const [artifactKeyHint, setArtifactKeyHint] = useState("");
  const [fallbackArtifactKey, setFallbackArtifactKey] = useState("");

  /* ---------------- Mint ---------------- */
  const [mintTo, setMintTo] = useState("");
  const [mintTokenId, setMintTokenId] = useState("");
  const [mintAmount, setMintAmount] = useState("");
  const [mintDataBytes, setMintDataBytes] = useState("0x");
  const [mintLoading, setMintLoading] = useState(false);
  const [mintStatus, setMintStatus] = useState("");
  const [acceptedMintRisk, setAcceptedMintRisk] = useState(false);

  /* ---------------- Balance ---------------- */
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState("");
  const [balanceValue, setBalanceValue] = useState(null);

  /* ---------------- Persisted Recent Tokens ---------------- */
  const [recentTokens, setRecentTokens] = useState(() => {
    return safeJsonParse(localStorage.getItem("chainforge_ops_recent") || "[]", []);
  });

  /* ---------------- Derived ---------------- */
  const addr = contractAddress.trim();
  const uiTokenType = detectedTokenType || mintTokenType;

  const needsTokenId = uiTokenType === "ERC721" || uiTokenType === "ERC1155";
  const needsAmount = uiTokenType === "ERC20" || uiTokenType === "ERC1155";
  const needsBytes = uiTokenType === "ERC1155";

  const tokenIdLabel =
    uiTokenType === "ERC721"
      ? "NFT Token ID"
      : uiTokenType === "ERC1155"
        ? "Token ID / Item ID"
        : "Token ID";

  const tokenIdHelpText =
    uiTokenType === "ERC721"
      ? "Each ERC721 Token ID is unique. This mints one NFT, not a quantity."
      : uiTokenType === "ERC1155"
        ? "For ERC1155, Token ID identifies the item/type. Quantity controls how many copies to mint."
        : "";

  const selectedNetworkObj = useMemo(() => {
    return networks.find((n) => n.key === selectedNetwork) || null;
  }, [networks, selectedNetwork]);

  const mainnetNetworks = useMemo(() => {
    return networks.filter((n) => !isTestnetNetwork(n));
  }, [networks]);

  const testnetNetworks = useMemo(() => {
    return networks.filter((n) => isTestnetNetwork(n));
  }, [networks]);

  useEffect(() => {
    if (walletAddress && !isEvmAddress(mintTo)) {
      setMintTo(walletAddress);
    }
  }, [walletAddress, mintTo]);

  useEffect(() => {
    if (!isAuthed) {
      setMintTo("");
    }
  }, [isAuthed]);

  const loadAccountUsage = useCallback(async () => {
    if (restoreLoading || !authLoading) {
      // continue only after auth has settled
    }

    if (restoreLoading || authLoading) {
      return;
    }

    if (!isAuthed) {
      setAccountUsage(null);
      return;
    }

    try {
      const res = await api.get("/account/usage", {
        headers: { "Cache-Control": "no-store" },
      });
      setAccountUsage(res.data || null);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAccountUsage(null);
        return;
      }
      setAccountUsage(null);
    }
  }, [isAuthed, restoreLoading, authLoading]);

  const tokenDisplayName = useMemo(() => {
    const onchainName = String(tokenInfo?.name || "").trim();
    const onchainSymbol = String(tokenInfo?.symbol || "").trim();

    const depName = String(
      tokenInfo?.deployment?.token?.tokenName ||
      tokenInfo?.tokenName ||
      ""
    ).trim();

    const depSymbol = String(
      tokenInfo?.deployment?.token?.tokenSymbol ||
      tokenInfo?.tokenSymbol ||
      ""
    ).trim();

    const name = onchainName || depName;
    const symbol = onchainSymbol || depSymbol;

    if (name && symbol) return `${name} (${symbol})`;
    if (name) return name;
    if (symbol) return symbol;

    if (uiTokenType === "ERC1155" && tokenInfo) {
      return `ERC1155 contract ${shorten(addr)}`;
    }

    if (uiTokenType === "ERC721" && tokenInfo) {
      return `ERC721 contract ${shorten(addr)}`;
    }

    if (uiTokenType === "ERC20" && tokenInfo) {
      return `ERC20 contract ${shorten(addr)}`;
    }

    return "";
  }, [tokenInfo, uiTokenType, addr]);

  const explorerContractUrl = useMemo(() => {
    return String(tokenInfo?.explorerUrl || "").trim();
  }, [tokenInfo]);

  const artifactKey = useMemo(() => {
    const fromTokenInfo =
      tokenInfo?.deployment?.artifactKey || tokenInfo?.deployment?.artifact_key || "";
    return String(fromTokenInfo || fallbackArtifactKey || "").trim();
  }, [tokenInfo, fallbackArtifactKey]);

  const isCustomMintToken = isCustomTokenInfo(tokenInfo);
  const customActionCapabilities = getCustomActionCapabilities(tokenInfo);
  const canCustomMint =
    isCustomMintToken &&
    (customActionCapabilities.mint === true ||
      customActionCapabilities.mintable === true);

  const canAttemptPrivateInference = Boolean(isAuthed && !authLoading && !restoreLoading);

  const MINT_PLAN_ACCESS = useMemo(
    () => ({
      ERC20: "pro",
      ERC721: "pro",
      ERC1155: "pro",
    }),
    [],
  );

  const currentPlan = normalizePlanCode(accountUsage?.plan || "free");
  const hasPaidAccess = hasPaidFeatureAccess(currentPlan);
  const isPrivilegedUser = Boolean(accountUsage?.isPrivileged);

  const isMintLocked = useCallback(
    (tokenType) => {
      if (isPrivilegedUser) return false;
      const requiredPlan = MINT_PLAN_ACCESS[tokenType] || "free";
      return requiredPlan === "pro" && !hasPaidAccess;
    },
    [MINT_PLAN_ACCESS, hasPaidAccess, isPrivilegedUser],
  );

  const mintBlockedReason = useMemo(() => {
    if (isMintLocked(uiTokenType)) return "Minting is available on Starter and Pro plans.";
    if (!walletAddress) return "Connect wallet from the top header to mint.";
    if (!isEvmAddress(addr)) return "Enter a valid contract address.";
    if (!isEvmAddress(mintTo)) return "Enter a valid 'Mint To' address.";
    if (needsTokenId && !isIntegerString(mintTokenId)) return "Token ID must be an integer.";
    if (needsAmount && !isNonEmptyString(mintAmount)) return "Amount is required.";

    if (uiTokenType === "ERC1155" && needsAmount && !isIntegerString(mintAmount)) {
      return "ERC1155 quantity must be an integer.";
    }

    if (needsBytes && !isHexBytes(mintDataBytes)) return "ERC1155 data must be 0x hex bytes.";

    if (isCustomMintToken) {
      if (!canCustomMint) {
        return "This custom contract does not expose a supported mint function.";
      }

      return "";
    }

    if (!isNonEmptyString(artifactKey)) return "Mint template (artifactKey) not found.";

    return "";
  }, [
    isMintLocked,
    uiTokenType,
    walletAddress,
    addr,
    mintTo,
    needsTokenId,
    mintTokenId,
    needsAmount,
    mintAmount,
    needsBytes,
    mintDataBytes,
    isCustomMintToken,
    canCustomMint,
    artifactKey,
  ]);

  const networkLabel = useMemo(() => {
    return selectedNetworkObj?.label || selectedNetwork || "-";
  }, [selectedNetworkObj, selectedNetwork]);

  const tokenDashboardLink = useMemo(() => {
    if (!selectedNetwork || !isEvmAddress(addr)) return "";
    return `/token/${encodeURIComponent(selectedNetwork)}/${encodeURIComponent(addr)}`;
  }, [selectedNetwork, addr]);

  const mintConfirmed = useMemo(() => {
    return String(mintStatus || "").toLowerCase().includes("confirmed");
  }, [mintStatus]);

  /* -------------------------------------------------------------------
   * Local persistence helpers
   * ----------------------------------------------------------------- */
  const saveRecentTokens = useCallback((list) => {
    setRecentTokens(list);
    try {
      localStorage.setItem("chainforge_ops_recent", JSON.stringify(list));
    } catch {
      // ignore
    }
  }, []);

  const resetWorkspace = useCallback(() => {
    setContractAddress("");
    setDetectedTokenType(null);
    setMintTokenType("ERC721");
    setDetectStatus("idle");
    setDetectMessage("");
    setTokenInfo(null);
    setTokenInfoError("");
    setFallbackArtifactKey("");
    setArtifactKeyHint("");
    setMintTokenId("");
    setMintAmount("");
    setMintDataBytes("0x");
    setMintStatus("");
    setBalanceError("");
    setBalanceValue(null);
    setAutoDetectEnabled(true);
    setMintTo(walletAddress || "");

    try {
      localStorage.removeItem("chainforge_ops_last");
    } catch {
      // ignore
    }
  }, [walletAddress]);

  const rememberCurrentToken = useCallback(() => {
    if (!isEvmAddress(addr)) return false;

    const entry = {
      network: selectedNetwork,
      address: addr,
      tokenType: uiTokenType || "",
      name: tokenDisplayName || "",
      explorerUrl: explorerContractUrl || "",
      savedAt: new Date().toISOString(),
    };

    const key = `${selectedNetwork}:${addr.toLowerCase()}`;
    const existing = Array.isArray(recentTokens) ? recentTokens : [];
    const next = [
      entry,
      ...existing.filter((x) => `${x.network}:${String(x.address).toLowerCase()}` !== key),
    ].slice(0, 15);

    saveRecentTokens(next);
    return true;
  }, [addr, selectedNetwork, uiTokenType, tokenDisplayName, explorerContractUrl, recentTokens, saveRecentTokens]);

  /* -------------------------------------------------------------------
   * Wallet txn helpers
   * ----------------------------------------------------------------- */
  const ensureWalletOnChain = useCallback(async (chainIdNumber) => {
    try {
      await switchActiveWalletChain(chainIdNumber);
    } catch (err) {
      if (err?.code === 4902) {
        throw new Error(
          "This network is not added in the connected wallet. Add it and try again.",
        );
      }

      if (err?.code === -32002) {
        throw new Error(
          "The wallet has a pending request. Open the wallet and complete it.",
        );
      }

      throw err;
    }
  }, []);

  const sendUserTransaction = useCallback(
    async (tx, from, chainId) => {
      return sendActiveWalletTransaction(tx, {
        chainId,
        expectedAddress: from,
      });
    },
    [],
  );

  const sleep = useCallback((ms) => new Promise((r) => setTimeout(r, ms)), []);

  const confirmWithRetry = useCallback(
    async (fn, { attempts = 6, delayMs = 2000 } = {}) => {
      let lastErr = null;
      for (let i = 0; i < attempts; i++) {
        try {
          return await fn();
        } catch (err) {
          lastErr = err;
          if (i === attempts - 1) throw err;
          await sleep(delayMs);
        }
      }
      throw lastErr || new Error("Confirm failed.");
    },
    [sleep],
  );

  /* -------------------------------------------------------------------
   * Load networks
   * ----------------------------------------------------------------- */
  useEffect(() => {
    let mounted = true;

    (async () => {
      setNetworksLoading(true);
      setNetworksError("");

      try {
        const res = await api.get("/networks", {
          params: { includeTestnets: true, onlyConfigured: true },
        });

        const list = res.data?.networks || [];
        if (!Array.isArray(list) || list.length === 0) {
          throw new Error("No networks returned from backend.");
        }

        if (!mounted) return;

        setNetworks(list);
        if (selectedNetwork && !list.some((n) => n.key === selectedNetwork)) {
          setSelectedNetwork("");
        }
      } catch (err) {
        if (!mounted) return;
        setNetworksError(safeApiError(err, "Failed to load networks."));
      } finally {
        if (mounted) setNetworksLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [selectedNetwork]);

  /* -------------------------------------------------------------------
   * Restore deep-link + last used
   * ----------------------------------------------------------------- */
  useEffect(() => {
    const { network, address } = readQueryParams();

    const last = safeJsonParse(localStorage.getItem("chainforge_ops_last") || "{}", {});
    const chosenNetwork = String(network || last?.network || "").trim();
    const chosenAddress = String(address || last?.address || "").trim();

    if (chosenNetwork) setSelectedNetwork(chosenNetwork);
    if (chosenAddress) setContractAddress(chosenAddress);
  }, []);

  /* -------------------------------------------------------------------
   * Persist last used
   * ----------------------------------------------------------------- */
  useEffect(() => {
    try {
      localStorage.setItem(
        "chainforge_ops_last",
        JSON.stringify({ network: selectedNetwork, address: addr }),
      );
    } catch {
      // ignore
    }
  }, [selectedNetwork, addr]);

  /* -------------------------------------------------------------------
   * Reset state when address/network changes
   * ----------------------------------------------------------------- */
  useEffect(() => {
    setFallbackArtifactKey("");
    setArtifactKeyHint("");
    setMintStatus("");
    setBalanceError("");
    setBalanceValue(null);
    setTokenInfoError("");
  }, [addr, selectedNetwork]);

  /* -------------------------------------------------------------------
   * Token detection
   * ----------------------------------------------------------------- */
  const detectTokenTypeFromBackend = useCallback(async ({ network, address }) => {
    const res = await api.get(`/token/${address}`, { params: { network } });
    const data = res.data || {};

    const fromDeployment =
      normalizeTokenType(data?.deployment?.tokenType) ||
      normalizeTokenType(data?.deployment?.standard) ||
      normalizeTokenType(data?.deployment?.type);
    if (fromDeployment) return { tokenType: fromDeployment, raw: data };

    const direct =
      normalizeTokenType(data.tokenType) ||
      normalizeTokenType(data.standard) ||
      normalizeTokenType(data.type);
    if (direct) return { tokenType: direct, raw: data };

    if (data.isERC1155 === true) return { tokenType: "ERC1155", raw: data };
    if (isNonEmptyString(data.uri) || isNonEmptyString(data.baseURI)) {
      return { tokenType: "ERC1155", raw: data };
    }
    if (data.decimals != null && String(data.decimals).trim() !== "") {
      return { tokenType: "ERC20", raw: data };
    }
    if (isNonEmptyString(data.name) && isNonEmptyString(data.symbol)) {
      return { tokenType: "ERC721", raw: data };
    }

    return { tokenType: null, raw: data };
  }, []);

  useEffect(() => {
    if (!autoDetectEnabled) return;

    if (!addr) {
      setDetectedTokenType(null);
      setDetectStatus("idle");
      setDetectMessage("");
      setTokenInfo(null);
      setTokenInfoError("");
      setArtifactKeyHint("");
      return;
    }

    if (!isEvmAddress(addr)) {
      setDetectedTokenType(null);
      setDetectStatus("failed");
      setDetectMessage("Enter a valid contract address (0x...) to auto-detect token type.");
      setTokenInfo(null);
      setArtifactKeyHint("");
      return;
    }

    const reqId = ++detectReqIdRef.current;
    setDetectStatus("detecting");
    setDetectMessage("");

    const t = setTimeout(async () => {
      try {
        const { tokenType } = await detectTokenTypeFromBackend({
          network: selectedNetwork,
          address: addr,
        });

        if (reqId !== detectReqIdRef.current) return;

        if (tokenType) {
          setDetectedTokenType(tokenType);
          setMintTokenType(tokenType);
          setDetectStatus("ok");
          setDetectMessage(`Detected: ${tokenType}`);
        } else {
          setDetectedTokenType(null);
          setDetectStatus("failed");
          setDetectMessage("Couldn’t detect token type. Choose token type manually.");
        }
      } catch (err) {
        if (reqId !== detectReqIdRef.current) return;
        setDetectedTokenType(null);
        setDetectStatus("failed");
        setDetectMessage(safeApiError(err, "Detection failed."));
      }
    }, 450);

    return () => clearTimeout(t);
  }, [addr, selectedNetwork, autoDetectEnabled, detectTokenTypeFromBackend]);

  /* -------------------------------------------------------------------
   * Token info load
   * ----------------------------------------------------------------- */
  useEffect(() => {
    if (!isEvmAddress(addr)) {
      setTokenInfo(null);
      setTokenInfoError("");
      return;
    }

    let mounted = true;

    (async () => {
      try {
        setTokenInfoLoading(true);
        setTokenInfoError("");

        const res = await api.get(`/token/${addr}`, {
          params: { network: selectedNetwork, viewer: walletAddress || "" },
        });

        if (!mounted) return;
        setTokenInfo(res.data || null);
      } catch (err) {
        if (!mounted) return;
        setTokenInfo(null);
        setTokenInfoError(safeApiError(err, "Failed to load token info."));
      } finally {
        if (mounted) setTokenInfoLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [addr, selectedNetwork, walletAddress]);

  /* -------------------------------------------------------------------
   * Artifact key fallback
   * ----------------------------------------------------------------- */
  useEffect(() => {
    if (!isEvmAddress(addr)) {
      setArtifactKeyHint("");
      setFallbackArtifactKey("");
      return;
    }

    if (
      isNonEmptyString(tokenInfo?.deployment?.artifactKey) ||
      isNonEmptyString(tokenInfo?.deployment?.artifact_key)
    ) {
      setFallbackArtifactKey("");
      setArtifactKeyHint("Template loaded from deployment metadata.");
      return;
    }

    let mounted = true;

    (async () => {
      setArtifactKeyLoading(true);
      setArtifactKeyHint("");

      try {
        if (!canAttemptPrivateInference) {
          setFallbackArtifactKey("");
          setArtifactKeyHint("Sign in from the top header to infer template from private history.");
          return;
        }

        const res = await api.get("/deployments", {
          params: { mine: true, kind: "deployment" },
        });
        const records = Array.isArray(res.data?.records) ? res.data.records : [];

        const match = records.find((r) => {
          const rNet = String(r?.network || "").toLowerCase();
          const rAddr = r?.address || r?.contractAddress || "";
          return rNet === String(selectedNetwork).toLowerCase() && eqAddr(rAddr, addr);
        });

        if (!mounted) return;

        const key = String(match?.artifactKey || match?.result?.artifactKey || "").trim();

        if (isNonEmptyString(key)) {
          setFallbackArtifactKey(key);
          setArtifactKeyHint("Template inferred from your private history.");
        } else {
          setFallbackArtifactKey("");
          setArtifactKeyHint(
            "Template not found. If this token wasn’t deployed via Krionex, mint may be unavailable.",
          );
        }
      } catch (err) {
        if (!mounted) return;

        if (err?.response?.status === 401) {
          setFallbackArtifactKey("");
          setArtifactKeyHint("Sign in from the top header to infer template from private history.");
          return;
        }

        setFallbackArtifactKey("");
        setArtifactKeyHint("Unable to load private history to infer template.");
      } finally {
        if (mounted) setArtifactKeyLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [addr, selectedNetwork, tokenInfo, canAttemptPrivateInference]);

  /* -------------------------------------------------------------------
   * Balance reader
   * ----------------------------------------------------------------- */
  const refreshBalance = useCallback(async () => {
    setBalanceError("");
    setBalanceValue(null);

    if (!walletAddress) {
      setBalanceError("Connect wallet from the top header to view balances.");
      return;
    }

    if (!isEvmAddress(addr)) {
      setBalanceError("Enter a valid contract address.");
      return;
    }

    if (!selectedNetwork) {
      setBalanceError("Select a network first.");
      return;
    }

    if (!uiTokenType) {
      setBalanceError("Select or detect the token standard first.");
      return;
    }

    if (uiTokenType === "ERC1155" && !isIntegerString(mintTokenId)) {
      setBalanceValue("Enter Token ID to view ERC1155 balance.");
      return;
    }

    setBalanceLoading(true);

    try {
      const res = await api.post("/balance", {
        network: selectedNetwork,
        tokenType: uiTokenType,
        contractAddress: addr,
        wallet: walletAddress,
        id: uiTokenType === "ERC1155" ? mintTokenId.trim() : undefined,
      });

      const data = res.data || {};

      if (!data.ok) {
        throw new Error(data.error || "Balance check failed.");
      }

      if (uiTokenType === "ERC20") {
        setBalanceValue(data.formattedBalance ?? data.balance ?? "0");
        return;
      }

      setBalanceValue(data.balance ?? "0");
    } catch (err) {
      setBalanceError(
        humanizeBalanceError(err, "Unable to fetch balance."),
      );
    } finally {
      setBalanceLoading(false);
    }
  }, [walletAddress, addr, selectedNetwork, uiTokenType, mintTokenId]);

  useEffect(() => {
    if (!walletAddress) return;
    if (!isEvmAddress(addr)) return;
    if (detectStatus === "detecting") return;
    if (tokenInfoLoading) return;
    if (uiTokenType === "ERC1155" && !isIntegerString(mintTokenId)) return;

    refreshBalance();
  }, [
    walletAddress,
    addr,
    uiTokenType,
    mintTokenId,
    detectStatus,
    tokenInfoLoading,
    refreshBalance,
  ]);

  useEffect(() => {
    setMintStatus("");
  }, [addr, mintTo, mintTokenId, mintAmount, mintDataBytes, selectedNetwork]);

  useEffect(() => {
    loadAccountUsage();
  }, [loadAccountUsage]);

  /* -------------------------------------------------------------------
   * Mint
   * ----------------------------------------------------------------- */
  const canMint = useMemo(() => {
    if (!isAuthed) return false;
    if (!acceptedMintRisk) return false;
    return !mintBlockedReason;
  }, [isAuthed, acceptedMintRisk, mintBlockedReason]);

  const handleMint = useCallback(async () => {
    setMintStatus("");

    if (!acceptedMintRisk) {
      setMintStatus(
        "Please accept the minting terms before minting. You are responsible for rights, legality, compliance, gas fees, wallet actions, and blockchain network conditions."
      );
      return;
    }

    if (!canMint) {
      setMintStatus(mintBlockedReason || "Please complete the required fields.");
      return;
    }

    const net = selectedNetworkObj;
    if (!net?.chainId) {
      setMintStatus("Selected network is missing chainId.");
      return;
    }

    setMintLoading(true);
    setMintStatus("Preparing mint transaction…");

    try {
      const isCustomMint = isCustomTokenInfo(tokenInfo);
      const actionCapabilities = getCustomActionCapabilities(tokenInfo);

      if (isCustomMint) {
        if (
          actionCapabilities.mint === false &&
          actionCapabilities.mintable === false
        ) {
          throw new Error("This custom contract does not expose a supported mint function.");
        }

        const customMintArgs = buildCustomMintArgs({
          tokenType: uiTokenType,
          to: mintTo.trim(),
          tokenId: mintTokenId.trim(),
          amount: mintAmount.trim(),
          data: mintDataBytes.trim(),
          tokenInfo,
        });

        setMintStatus("Preparing custom mint transaction…");

        const prep = await prepareCustomTokenAction({
          tokenInfo,
          network: selectedNetwork,
          contractAddress: addr,
          action: "mint",
          args: customMintArgs,
        });

        setMintStatus(`Switching wallet to ${net.label}…`);
        await ensureWalletOnChain(net.chainId);

        setMintStatus("Confirm the custom mint in your wallet…");
        const txHash = await sendUserTransaction(
          prep.tx,
          walletAddress,
          net.chainId,
        );

        setMintStatus(`Mint tx sent (${shorten(txHash)}). Confirming…`);

        const confirmData = await confirmWithRetry(
          () =>
            confirmCustomTokenAction({
              actionRequestId: prep.actionRequestId,
              txHash,
              confirmations: net.confirmations || 2,
            }),
          { attempts: 6, delayMs: 2000 },
        );

        const ok = Boolean(confirmData?.ok);
        setMintStatus(ok ? "Custom mint confirmed ✅" : "Custom mint submitted.");

        rememberCurrentToken();

        if (uiTokenType === "ERC1155") {
          const mintedId = (
            confirmData?.tokenId ??
            confirmData?.actionHistory?.tokenId ??
            mintTokenId ??
            ""
          )
            .toString()
            .trim();

          if (isIntegerString(mintedId) && !isNonEmptyString(mintTokenId)) {
            setMintTokenId(mintedId);
          }
        }

        refreshBalance();
        return;
      }

      const prepRes = await api.post("/mint", {
        network: selectedNetwork,
        mode: "user",
        tokenType: uiTokenType,
        artifactKey,
        contractAddress: addr,
        to: mintTo.trim(),
        userAddress: walletAddress,
        tokenId: uiTokenType === "ERC721" ? mintTokenId.trim() : undefined,
        amount: uiTokenType !== "ERC721" ? mintAmount.trim() : undefined,
        amountFormat: uiTokenType === "ERC20" ? "human" : undefined,
        id: uiTokenType === "ERC1155" ? mintTokenId.trim() : undefined,
        data: uiTokenType === "ERC1155" ? mintDataBytes.trim() : undefined,
      });

      const prep = prepRes.data;
      if (!prep?.ok || !prep?.tx?.data) {
        throw new Error(prep?.error || "Backend did not return mint tx.");
      }

      setMintStatus(`Switching wallet to ${net.label}…`);
      await ensureWalletOnChain(net.chainId);

      setMintStatus("Confirm the mint in your wallet…");
      const txHash = await sendUserTransaction(
        prep.tx,
        walletAddress,
        net.chainId,
      );

      setMintStatus(`Mint tx sent (${shorten(txHash)}). Confirming…`);

      const confirmRes = await confirmWithRetry(
        () =>
          api.post("/mint/confirm", {
            network: selectedNetwork,
            txHash,
            contractAddress: addr,
            userAddress: walletAddress,
            to: mintTo.trim(),
            tokenType: uiTokenType,
            artifactKey,
            tokenId: mintTokenId ? mintTokenId.trim() : null,
            amount: mintAmount ? mintAmount.trim() : null,
            amountFormat: uiTokenType === "ERC20" ? "human" : undefined,
            decimals:
              uiTokenType === "ERC20"
                ? (tokenInfo?.decimals ??
                  tokenInfo?.deployment?.token?.decimals ??
                  18)
                : undefined,
            data: uiTokenType === "ERC1155" ? mintDataBytes.trim() : undefined,
            confirmations: net.confirmations || 2,
          }),
        { attempts: 6, delayMs: 2000 },
      );

      const ok = Boolean(confirmRes?.data?.ok);
      setMintStatus(ok ? "Mint confirmed ✅" : "Mint submitted (confirmation pending).");

      rememberCurrentToken();

      if (uiTokenType === "ERC1155") {
        const mintedId = (confirmRes?.data?.tokenId ?? confirmRes?.data?.id ?? "").toString().trim();
        if (isIntegerString(mintedId) && !isNonEmptyString(mintTokenId)) {
          setMintTokenId(mintedId);
        }
      }

      refreshBalance();
    } catch (err) {
      const code = err?.code;
      const nestedCode = err?.info?.error?.code;
      const message = String(
        err?.shortMessage ||
        err?.reason ||
        err?.message ||
        err?.info?.error?.message ||
        "",
      ).toLowerCase();

      const isRejected =
        code === "ACTION_REJECTED" ||
        code === 4001 ||
        nestedCode === 4001 ||
        nestedCode === 5000 ||
        message.includes("user denied") ||
        message.includes("user rejected") ||
        message.includes("rejected") ||
        message.includes("denied transaction signature");

      if (!isRejected) {
        console.error("[operations:mint] failed:", err);
      }

      setMintStatus(humanizeWalletActionError(err, "Mint operation failed."));
    } finally {
      setMintLoading(false);
    }
  }, [
    acceptedMintRisk,
    canMint,
    mintBlockedReason,
    selectedNetworkObj,
    selectedNetwork,
    uiTokenType,
    artifactKey,
    addr,
    mintTo,
    mintTokenId,
    mintAmount,
    mintDataBytes,
    walletAddress,
    tokenInfo,
    ensureWalletOnChain,
    sendUserTransaction,
    rememberCurrentToken,
    refreshBalance,
    confirmWithRetry,
  ]);

  /* -------------------------------------------------------------------
   * UI
   * ----------------------------------------------------------------- */
  const showMintGate = !walletAddress;
  const showPrivateInferenceGate = !canAttemptPrivateInference;

  const tokenInfoTone =
    tokenInfoError
      ? "error"
      : tokenInfoLoading
        ? "neutral"
        : tokenDisplayName
          ? "success"
          : "neutral";

  return (
    <div className="space-y-6">
      {!isAuthed && (
        <InlineNotice
          title="Sign in to activate Operations"
          subtitle="Preview the workspace below. After sign-in, Krionex can load private deployment history, resolve mint templates, read balances, and prepare wallet-confirmed actions."
          tone="warning"
        />
      )}<div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.10),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(15,23,42,0.06),_transparent_26%),linear-gradient(to_bottom,_#F7F8FA,_#F1F3F6_42%,_#F7F8FA)]" />

      <div className="relative overflow-hidden rounded-[32px] border border-[#2A2418] bg-[#050713] p-6 text-white shadow-[0_24px_70px_rgba(5,7,19,0.28)] sm:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.24),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050713] via-[#050713]/95 to-[#111827]/90" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E6C978]/60 to-transparent" />
        <div className="relative">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center rounded-full border border-[#E6C978]/35 bg-[#E6C978]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#F8E7B0] backdrop-blur">
                Contract Operations
              </div>

              <h1 className="mt-4 max-w-3xl text-3xl font-semibold tracking-[-0.045em] text-white sm:text-4xl">
                Manage token actions from one production-ready interface
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                Load an existing contract, detect token standards, infer mint templates from
                Krionex history, and execute mint transactions directly from the connected wallet.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                <StatusPill
                  label="Session"
                  value={authLoading ? "Checking…" : isAuthed ? "Signed in" : "Not signed in"}
                  tone={authLoading ? "neutral" : isAuthed ? "success" : "warning"}
                />
                <StatusPill
                  label="Wallet"
                  value={walletAddress ? shorten(walletAddress) : "Not connected"}
                  tone={walletAddress ? "brand" : "warning"}
                />
                <StatusPill label="Network" value={networkLabel} tone="neutral" />
                <StatusPill label="Mode" value={uiTokenType} tone="brand" />
              </div>
            </div>

            <div className="grid w-full gap-3 sm:grid-cols-3 xl:max-w-md">
              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
                  Contract
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
                  {isEvmAddress(addr) ? shorten(addr) : "Not loaded"}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-300">
                  Selected contract for reads and mint actions.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
                  Standard
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
                  {uiTokenType || "-"}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-300">
                  Active token standard for reads and actions.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">
                  Balance
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight text-white">
                  {balanceLoading ? "…" : balanceValue || "-"}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-300">
                  {uiTokenType === "ERC1155" ? "Depends on Token ID." : "Connected wallet balance."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>


      {authLoading ? (
        <InlineNotice
          title="Checking session"
          subtitle="Verifying your session and loading account-aware features."
        />
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_380px]">
        <div className="space-y-6">
          <SectionCard
            title="Contract Workspace"
            subtitle="Choose a network, load a contract, and prepare the active token context."
            right={
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                  <input
                    type="checkbox"
                    checked={autoDetectEnabled}
                    onChange={(e) => setAutoDetectEnabled(e.target.checked)}
                  />
                  Auto detect token type
                </label>

                <SecondaryButton
                  onClick={resetWorkspace}
                  className="px-3 py-2 text-xs"
                >
                  New Token
                </SecondaryButton>
              </div>
            }
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Network</Label>
                {networksLoading ? (
                  <InlineNotice
                    title="Loading networks"
                    subtitle="Fetching configured networks from the backend."
                  />
                ) : networksError ? (
                  <InlineNotice
                    title="Unable to load networks"
                    subtitle={networksError}
                    tone="error"
                  />
                ) : (

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setNetworkPickerOpen((v) => !v)}
                      className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm hover:border-indigo-300"
                    >
                      <div className="flex items-center gap-3">
                        {(() => {
                          const meta = getNetworkMeta(selectedNetworkObj);
                          return (
                            <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${meta.tone}`}>
                              {meta.logo ? (
                                <img src={meta.logo} alt="" className="h-7 w-7 rounded-full" />
                              ) : (
                                <span className="text-xs font-bold">
                                  {getNetworkShortName(selectedNetworkObj)}
                                </span>
                              )}
                            </div>
                          );
                        })()}

                        <div>
                          <div className="text-sm font-semibold">
                            {selectedNetworkObj?.label || "Select network"}
                          </div>
                          <div className="text-xs text-slate-500">
                            {selectedNetworkObj?.chainId ? `Chain ID ${selectedNetworkObj.chainId}` : "Choose a network"}
                          </div>
                        </div>
                      </div>

                      <span>⌄</span>
                    </button>

                    {networkPickerOpen && (
                      <div className="absolute z-30 mt-2 w-full rounded-xl border bg-white p-2 shadow-xl max-h-64 overflow-y-auto">
                        <div className="mb-3 grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1">
                          <button
                            type="button"
                            onClick={() => setNetworkPickerTab("mainnets")}
                            className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${networkPickerTab === "mainnets"
                              ? "bg-white text-indigo-700 shadow-sm"
                              : "text-slate-600 hover:text-slate-900"
                              }`}
                          >
                            Mainnets
                          </button>

                          <button
                            type="button"
                            onClick={() => setNetworkPickerTab("testnets")}
                            className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${networkPickerTab === "testnets"
                              ? "bg-white text-amber-700 shadow-sm"
                              : "text-slate-600 hover:text-slate-900"
                              }`}
                          >
                            Testnets
                          </button>
                        </div>

                        {(networkPickerTab === "mainnets" ? mainnetNetworks : testnetNetworks).map((n) => (
                          <button
                            key={n.key}
                            onClick={() => {
                              setSelectedNetwork(n.key);
                              setNetworkPickerOpen(false);
                            }}
                            className="flex w-full items-center gap-2 p-2 hover:bg-slate-100 rounded-lg"
                          >
                            <span>{n.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                )}
              </div>

              <div>
                <Label>Token Standard</Label>
                <Select
                  value={mintTokenType}
                  onChange={(e) => setMintTokenType(e.target.value)}
                  disabled={autoDetectEnabled && Boolean(detectedTokenType)}
                  title={
                    autoDetectEnabled && detectedTokenType
                      ? "Token type is auto-detected. Disable auto-detect to override."
                      : ""
                  }
                >
                  <option value="ERC20">ERC20</option>
                  <option value="ERC721">ERC721</option>
                  <option value="ERC1155">ERC1155</option>
                </Select>
              </div>
            </div>

            <div className="mt-4">
              <Label>Contract Address</Label>
              <div className="flex flex-col gap-3 md:flex-row">
                <Input
                  placeholder="Paste contract address (0x...)"
                  value={contractAddress}
                  onChange={(e) => setContractAddress(e.target.value)}
                />
              </div>

              <p className="mt-2 text-xs text-slate-500">
                Use New Token to clear the current contract, token info, mint fields, and saved workspace state.
              </p>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
              <div className="rounded-2xl border border-sky-100 bg-gradient-to-br from-white to-sky-50/60 p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Detection & Token Info
                  </div>

                  {explorerContractUrl ? (
                    <a
                      href={explorerContractUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Open Explorer
                    </a>
                  ) : null}
                </div>

                {autoDetectEnabled ? (
                  <div className="mb-3 text-xs">
                    {detectStatus === "detecting" ? (
                      <span className="text-slate-500">Detecting token type…</span>
                    ) : detectStatus === "ok" ? (
                      <span className="font-medium text-emerald-700">{detectMessage}</span>
                    ) : detectStatus === "failed" ? (
                      <span className="font-medium text-amber-700">{detectMessage}</span>
                    ) : null}
                  </div>
                ) : null}

                {tokenInfoTone === "error" ? (
                  <InlineNotice
                    title="Unable to load token info"
                    subtitle={tokenInfoError}
                    tone="error"
                  />
                ) : tokenInfoLoading ? (
                  <InlineNotice
                    title="Loading token info"
                    subtitle="Reading contract details for the selected address."
                  />
                ) : tokenDisplayName ? (
                  <InlineNotice
                    title="Token loaded"
                    subtitle={`You’re interacting with ${tokenDisplayName}.`}
                    tone="success"
                  />
                ) : (
                  <InlineNotice
                    title="No contract loaded"
                    subtitle="Paste a valid contract address to load token details and enable operations."
                  />
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Quick Access
                </div>

                <div className="mt-4 space-y-3">
                  {isEvmAddress(addr) ? (
                    <Link
                      to={`/token/${selectedNetwork}/${addr}`}
                      className="block rounded-2xl border border-slate-200 bg-gradient-to-r from-white to-indigo-50 px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:from-indigo-50 hover:to-sky-50"
                    >
                      Open Token Dashboard
                    </Link>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-3 text-sm text-slate-400">
                      Token Dashboard link appears after a valid token address is loaded.
                    </div>
                  )}

                  <Link
                    to="/deployments"
                    className="block rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    View Deployment Records
                  </Link>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Mint Execution"
            subtitle="Prepare a mint transaction and execute it directly from the connected wallet."
            right={
              <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                {isCustomMintToken ? "Active custom:" : "Active standard:"}{" "}
                <span className="text-slate-900">{uiTokenType}</span>
              </div>
            }
          >
            <div className="space-y-5">
              {showMintGate ? (
                <InlineNotice
                  title="Wallet required"
                  subtitle="Connect your wallet from the top header to prepare and sign mint transactions."
                  tone="warning"
                />
              ) : null}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label>Recipient Address</Label>
                  <Input
                    placeholder="Recipient address (0x...)"
                    value={mintTo}
                    onChange={(e) => setMintTo(e.target.value)}
                  />
                </div>

                {needsTokenId ? (
                  <div>
                    <Label>{tokenIdLabel}</Label>
                    <Input
                      placeholder={tokenIdLabel}
                      value={mintTokenId}
                      onChange={(e) => setMintTokenId(e.target.value)}
                    />
                    {tokenIdHelpText ? (
                      <p className="mt-2 text-xs leading-5 text-slate-500">
                        {tokenIdHelpText}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {needsAmount ? (
                  <div>
                    <Label>{uiTokenType === "ERC20" ? "Amount" : "Quantity"}</Label>
                    <Input
                      placeholder={uiTokenType === "ERC20" ? "Amount" : "Quantity"}
                      value={mintAmount}
                      onChange={(e) => setMintAmount(e.target.value)}
                    />
                    {uiTokenType === "ERC1155" ? (
                      <p className="mt-2 text-xs leading-5 text-slate-500">
                        Quantity is the number of copies to mint for this ERC1155 Token ID.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {needsBytes ? (
                  <div className="md:col-span-2">
                    <Label>Data Bytes</Label>
                    <Input
                      placeholder="Data bytes (0x...)"
                      value={mintDataBytes}
                      onChange={(e) => setMintDataBytes(e.target.value)}
                    />
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      Advanced optional ERC1155 field. Leave as 0x unless your contract requires custom data.
                    </p>
                  </div>
                ) : null}
              </div>

              {!canMint && mintBlockedReason ? (
                <InlineNotice
                  title={
                    mintBlockedReason === "Minting is available on Starter and Pro plans."
                      ? "Starter or Pro access required"
                      : "Action not ready"
                  }
                  subtitle={mintBlockedReason}
                  tone="warning"
                />
              ) : null}

              {mintStatus ? (
                <InlineNotice
                  title={mintConfirmed ? "Mint confirmed" : "Mint status"}
                  subtitle={
                    mintConfirmed
                      ? "Mint completed successfully. Open Token Dashboard to review confirmed token actions, balance, pause state, and transaction history."
                      : mintStatus
                  }
                  tone={
                    mintConfirmed
                      ? "success"
                      : String(mintStatus).toLowerCase().includes("failed")
                        ? "error"
                        : "neutral"
                  }
                  actions={
                    mintConfirmed && tokenDashboardLink ? (
                      <Link
                        to={tokenDashboardLink}
                        className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-50"
                      >
                        Open Token Dashboard
                      </Link>
                    ) : null
                  }
                />
              ) : null}

              <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-[#F8FAFC] px-4 py-3 text-[13px] leading-6 text-slate-700 shadow-sm">
                <input
                  type="checkbox"
                  checked={acceptedMintRisk}
                  onChange={(e) => {
                    setAcceptedMintRisk(e.target.checked);
                    if (e.target.checked) setMintStatus("");
                  }}
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                />
                <span>
                  I confirm that I have the rights and legal authority to mint this asset/token.
                  I understand that blockchain transactions are irreversible, gas fees are
                  non-refundable, and Krionex is not responsible for wallet issues, user-side
                  network issues, RPC failures, intellectual property disputes, regulatory
                  issues, or blockchain network conditions.{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-[#8A641E] hover:text-[#050713]"
                  >
                    Terms
                  </Link>
                </span>
              </label>

              <div className="flex flex-col gap-3 sm:flex-row">
                <PrimaryButton
                  onClick={handleMint}
                  disabled={!canMint || mintLoading}
                  className="sm:min-w-[180px]"
                  title={!canMint ? mintBlockedReason : ""}
                >
                  {mintLoading
                    ? "Processing…"
                    : isMintLocked(uiTokenType)
                      ? "Pro Required"
                      : "Execute Mint"}
                </PrimaryButton>

                <SecondaryButton
                  onClick={refreshBalance}
                  disabled={!walletAddress}
                  className="sm:min-w-[160px]"
                >
                  Refresh Balance
                </SecondaryButton>
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-6">
          <SectionCard
            title="Wallet Summary"
            subtitle="Active session and on-chain context for this workspace."
          >
            <div className="space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Session
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {authLoading ? "Checking…" : isAuthed ? "Signed in" : "Not signed in"}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Wallet
                </div>
                <div className="mt-2 break-all text-sm font-semibold text-slate-900">
                  {walletAddress || "No wallet connected"}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Selected Network
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {networkLabel}
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Mint Availability"
            subtitle="Standard tokens need a Krionex deployment template. Custom Krionex deployments use the stored runtime ABI."
            right={
              artifactKeyLoading ? (
                <span className="text-xs font-medium text-slate-500">Loading…</span>
              ) : null
            }
          >
            <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
              <div className="break-all text-sm font-semibold text-slate-900">
                {isCustomMintToken
                  ? canCustomMint
                    ? "Ready for custom mint"
                    : "Custom mint unavailable"
                  : artifactKey
                    ? "Ready"
                    : "Not available"}
              </div>

              {artifactKeyHint ? (
                <p className="mt-2 text-xs leading-5 text-slate-500">{artifactKeyHint}</p>
              ) : null}
            </div>

            {!isCustomMintToken && !artifactKey && showPrivateInferenceGate ? (
              <div className="mt-4">
                <InlineNotice
                  title="Private inference unavailable"
                  subtitle="Sign in from the top header to infer template details from your private deployment history."
                  tone="warning"
                />
              </div>
            ) : null}
          </SectionCard>

          <SectionCard
            title="Balance"
            subtitle="Read the connected wallet balance for the active contract."
            right={
              <button
                onClick={refreshBalance}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
              >
                Refresh
              </button>
            }
          >
            {!walletAddress ? (
              <InlineNotice
                title="Wallet connection required"
                subtitle="Connect from the top header to read balances for the active contract."
                tone="warning"
              />
            ) : (
              <div className="space-y-3">
                <div className="text-3xl font-semibold tracking-tight text-slate-900">
                  {balanceLoading ? "…" : balanceValue || "-"}
                </div>

                {balanceError ? (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-red-600">{balanceError}</p>
                    <p className="text-xs text-slate-500">
                      Try confirming the selected network and token standard, or open the token dashboard to verify the loaded contract details.
                    </p>
                  </div>
                ) : null}

                {uiTokenType === "ERC1155" ? (
                  <p className="text-xs leading-5 text-slate-500">
                    ERC1155 balances depend on Token ID. Enter a Token ID in the mint panel to load the correct balance.
                  </p>
                ) : null}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Recent Tokens"
            subtitle="Quickly reopen contracts you’ve used recently."
            right={
              Array.isArray(recentTokens) && recentTokens.length > 0 ? (
                <button
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                  onClick={() => saveRecentTokens([])}
                >
                  Clear
                </button>
              ) : null
            }
          >
            {Array.isArray(recentTokens) && recentTokens.length > 0 ? (
              <div className="space-y-3">
                {recentTokens.slice(0, 5).map((t, idx) => (
                  <button
                    key={idx}
                    className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#E6D3A3] hover:bg-[#FFFDF7] hover:shadow-md"
                    onClick={() => {
                      const nextNetwork = String(t.network || "").trim();
                      const nextAddress = String(t.address || "").trim();

                      setSelectedNetwork(nextNetwork);
                      setContractAddress(nextAddress);

                      if (nextNetwork && isEvmAddress(nextAddress)) {
                        navigate(`/token/${encodeURIComponent(nextNetwork)}/${encodeURIComponent(nextAddress)}`);
                      }
                    }}
                  >
                    <div className="text-sm font-semibold text-slate-900">
                      {t.name ? t.name : shorten(String(t.address || ""))}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {String(t.tokenType || "-")} • {String(t.network || "-")}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                No recent tokens saved yet.
              </div>
            )}
          </SectionCard>

          <Surface className="overflow-hidden border border-[#1F2937]">
            <div className="bg-[#050713] p-6 text-white">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#E6C978]">
                Need help?
              </div>
              <div className="mt-3 text-xl font-semibold tracking-tight">
                Keep operations moving
              </div>
              <p className="mt-2 text-sm leading-6 text-white/85">
                Use support if a token was not deployed through Krionex, template inference is missing,
                or mint preparation needs platform review.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/help-center"
                  className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
                >
                  Open Help Center
                </Link>
                <Link
                  to="/upgrade"
                  className="inline-flex items-center justify-center rounded-2xl border border-white/20 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </Surface>
        </div>
      </div>
    </div>
  );
}